import { Fragment, useMemo, useState } from "react";
import type { ComponentProps, ReactNode } from "react";

import { useLocalization } from "@fluent/react";
import {
  Button,
  Chip,
  Popover,
  Row,
  useBreakpoints,
} from "@narmi/design_system";

import { useFilterContext } from "../../context/FilterContext";
import SearchFilterPanel from "../Filters/SearchFilterPanel";

import styles from "./FilterBar.module.scss";

export interface FilterBarFilter {
  id: string;
  label: string;
  labelOverride?: string;
  startIcon?: string;
  count?: string | number;
  minBreakpoint?: "*" | "m" | "l";
  renderPanel: (close: () => void) => ReactNode;
  alwaysVisible?: boolean;
  isActive?: boolean;
  onRemove?: () => void;
}

export type FilterBarSearch = false | "box" | "chip";

export interface FilterBarProps {
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClearFilter?: () => void;
  filters: FilterBarFilter[];
  search?: FilterBarSearch;
  actions?: ReactNode;
  hasDividerAfterFirst?: boolean;
}

const MORE_FILTERS_ID = "__more_filters__";

const FilterBar = ({
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onClearFilter,
  filters,
  search = "box",
  actions,
  hasDividerAfterFirst = false,
}: FilterBarProps) => {
  const { l10n } = useLocalization();

  const allFilters = useMemo<FilterBarFilter[]>(() => {
    if (search !== "chip") return filters;
    return [
      {
        id: "search",
        label: l10n.getString("live-ledger-filter-bar-keyword"),
        startIcon: "search",
        alwaysVisible: true,
        renderPanel: () => (
          <div className={styles.searchChipPanel}>
            <SearchFilterPanel
              value={searchValue}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
            />
          </div>
        ),
      },
      ...filters,
    ];
  }, [search, filters, l10n, searchValue, onSearchChange, searchPlaceholder]);

  const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
  const [visibleFilterIds, setVisibleFilterIds] = useState<Set<string>>(
    () =>
      new Set(
        allFilters
          .filter((f) => !f.alwaysVisible && f.isActive)
          .map((f) => f.id)
      )
  );
  const breakpoints = useBreakpoints();

  const { hasAnyActive, clearAllFilters } = useFilterContext();

  const closePanel = () => setActiveFilterId(null);

  const togglableFilters = allFilters.filter((f) => !f.alwaysVisible);

  const renderedFilters = allFilters.filter(
    ({ id, alwaysVisible, isActive, minBreakpoint = "*" }) => {
      const passesBreakpoint =
        minBreakpoint === "*" || breakpoints[minBreakpoint];
      if (alwaysVisible) return passesBreakpoint;
      return passesBreakpoint && (visibleFilterIds.has(id) || isActive);
    }
  );

  const handleToggleFilter = (filter: FilterBarFilter) => {
    if (visibleFilterIds.has(filter.id)) {
      filter.onRemove?.();
      setVisibleFilterIds((prev) => {
        const next = new Set(prev);
        next.delete(filter.id);
        return next;
      });
      closePanel();
    } else {
      setVisibleFilterIds((prev) => new Set(prev).add(filter.id));
      setActiveFilterId(filter.id);
    }
  };

  const handleClearFilter = () => {
    clearAllFilters();
    closePanel();
    onClearFilter?.();
  };

  return (
    <div className={styles.filterBar}>
      {search === "box" && (
        <SearchFilterPanel
          value={searchValue}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
        />
      )}
      {(allFilters.length > 0 || hasAnyActive) && (
        <div className={styles.chipRow}>
          <div className={styles.chipGroup}>
            {renderedFilters.map((filter, index) => {
              const isOpen = filter.id === activeFilterId;
              return (
                <Fragment key={filter.id}>
                  {hasDividerAfterFirst && index === 1 && (
                    <div className={styles.chipDivider} aria-hidden="true" />
                  )}
                  <div data-testid={`filter-bar-chip-${filter.id}`}>
                    <Popover
                      content={filter.renderPanel(closePanel)}
                      isOpen={isOpen}
                      onUserEnable={() => setActiveFilterId(filter.id)}
                      onUserDismiss={closePanel}
                      testId={`filter-bar-popover-${filter.id}`}
                      renderTrigger={() => (
                        <Chip
                          kind="secondary"
                          label={filter.labelOverride ?? filter.label}
                          startIcon={
                            filter.startIcon as ComponentProps<
                              typeof Chip
                            >["startIcon"]
                          }
                          endIcon={isOpen ? "chevron-up" : "chevron-down"}
                          count={filter.count}
                          onClick={() => {}}
                        />
                      )}
                    />
                  </div>
                </Fragment>
              );
            })}
            {togglableFilters.length > 0 && (
              <div
                data-testid="filter-bar-more-filters"
                className={styles.moreFiltersChip}
              >
                <Popover
                  content={
                    <div className={styles.moreFiltersMenu}>
                      {togglableFilters.map((filter) => (
                        <button
                          type="button"
                          key={filter.id}
                          data-testid={`more-filters-item-${filter.id}`}
                          className={styles.moreFiltersItem}
                          onClick={() => handleToggleFilter(filter)}
                        >
                          <span>{filter.label}</span>
                          {visibleFilterIds.has(filter.id) && (
                            <Row.Item shrink>
                              <i className="narmi-icon-check" />
                            </Row.Item>
                          )}
                        </button>
                      ))}
                    </div>
                  }
                  isOpen={activeFilterId === MORE_FILTERS_ID}
                  onUserEnable={() => setActiveFilterId(MORE_FILTERS_ID)}
                  onUserDismiss={closePanel}
                  renderTrigger={() => (
                    <Chip
                      kind="secondary"
                      label={l10n.getString(
                        "live-ledger-filter-bar-more-filters"
                      )}
                      isLabelVisible={false}
                      startIcon="filter-2"
                      onClick={() => {}}
                    />
                  )}
                />
              </div>
            )}
          </div>
          {(hasAnyActive || actions) && (
            <div className={styles.chipRowActions}>
              {hasAnyActive && (
                <Button
                  kind="plain"
                  size="xs"
                  type="button"
                  label={l10n.getString("live-ledger-filter-bar-clear-filter")}
                  onClick={handleClearFilter}
                  testId="filter-bar-clear-filter"
                />
              )}
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
